import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { captureBoundaryError } from "@/lib/sentry";
import { isChunkLoadError, recoverFromChunkError } from "@/lib/chunkReload";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  resetKey?: string;
}

interface State {
  hasError: boolean;
  // The page's code could not be downloaded — almost always a tab that was
  // open across a deploy (see lib/chunkReload.ts), not a bug in the page.
  staleBuild: boolean;
}

// App-wide safety net: a render-time exception anywhere below this boundary
// shows a friendly fallback (and reports to Sentry) instead of a blank white
// screen. Without it, one unguarded `x.y.z` on null silently kills the whole
// page — which is exactly the Settings render-blank symptom we saw in QA.
class ErrorBoundaryInner extends Component<Props, State> {
  state: State = {hasError: false, staleBuild: false};

  static getDerivedStateFromError(error: unknown): State {
    return {hasError: true, staleBuild: isChunkLoadError(error)};
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // A stale tab reloads onto the new build; that is expected after every
    // deploy, so only report it when the reload did not cure it.
    if (isChunkLoadError(error) && recoverFromChunkError() === "reloading") return;
    captureBoundaryError(error, info);
  }

  componentDidUpdate(prevProps: Props) {
    // Recover on navigation: once the route changes, clear the error so the new
    // page renders instead of staying stuck on the fallback until a full reload.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({hasError: false, staleBuild: false});
    }
  }

  // Reload straight away. Clearing the error first rendered the crashed page
  // again, which fired all its queries only for the reload to cancel them
  // (each one a "Failed to fetch" in Sentry), and could crash it again.
  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback staleBuild={this.state.staleBuild} onReload={this.handleReload} />;
    }
    return this.props.children;
  }
}

function ErrorFallback({staleBuild, onReload}: {staleBuild: boolean; onReload: () => void}) {
  const {t} = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md text-center space-y-4">
        <h1 className="text-xl font-display font-semibold text-foreground">
          {staleBuild ? t("errorBoundary.updatedTitle") : t("errorBoundary.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {staleBuild ? t("errorBoundary.updatedBody") : t("errorBoundary.body")}
        </p>
        <Button onClick={onReload}>{t("errorBoundary.reload")}</Button>
      </div>
    </div>
  );
}

// Wrap the class boundary so it resets on route change. It lives inside the
// router (AppShell.tsx — BrowserRouter in the browser, StaticRouter in SSR), so
// useLocation is valid; the default export keeps the `{ children }` signature.
export default function ErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundaryInner resetKey={location.pathname}>{children}</ErrorBoundaryInner>;
}
